import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { auth } from '../firebase';
import { Link } from 'react-router-dom';

type Status = 'loading' | 'success' | 'error' | 'expired';

const EmailBestaetigtPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('loading');
    const [errorMsg, setErrorMsg] = useState('');
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        const oobCode = searchParams.get('oobCode');
        const mode = searchParams.get('mode');

        // Fallback: Wenn der Benutzer bereits verifiziert ist, zeige Erfolg
        if (auth.currentUser?.emailVerified) {
            setStatus('success');
            return;
        }

        // Wenn kein Code oder falscher Modus, dann Fehler
        if (!oobCode || mode !== 'verifyEmail') {
            setStatus('error');
            setErrorMsg('Ungültiger oder fehlender Bestätigungslink.');
            return;
        }

        const verify = async () => {
            try {
                // Prüfe, ob der Code noch gültig ist
                await checkActionCode(auth, oobCode);
                // Wende den Code an (bestätigt die E-Mail)
                await applyActionCode(auth, oobCode);
                // Lade den Benutzer neu
                if (auth.currentUser) {
                    await auth.currentUser.reload();
                }
                setStatus('success');
            } catch (err: any) {
                // Spezifische Fehler abfangen
                if (
                    err.code === 'auth/invalid-action-code' ||
                    err.code === 'auth/expired-action-code'
                ) {
                    setStatus('expired');
                } else {
                    setStatus('error');
                    setErrorMsg(err.message || 'Ein unbekannter Fehler ist aufgetreten.');
                }
            }
        };

        verify();
    }, [searchParams, navigate]);

    // Automatische Weiterleitung bei Erfolg
    useEffect(() => {
        if (status !== 'success') return;
        const timer = setInterval(() => {
            setCountdown(c => {
                if (c <= 1) {
                    clearInterval(timer);
                    navigate('/lernsets');
                }
                return c - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [status, navigate]);

    // Rendering (unverändert, aber vollständig)
    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
                <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
                />
                <motion.div
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
                    className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
                />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                <AnimatePresence mode="wait">

                    {/* Loading */}
                    {status === 'loading' && (
                        <motion.div
                            key="loading"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 p-12 text-center"
                        >
                            <div className="flex justify-center mb-6">
                                <div className="w-16 h-16 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                                    <svg className="w-7 h-7 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                    </svg>
                                </div>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Bestätigung wird verarbeitet…</h2>
                            <p className="text-gray-500 text-sm">Bitte einen Moment warten.</p>
                        </motion.div>
                    )}

                    {/* Success */}
                    {status === 'success' && (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 overflow-hidden"
                        >
                            <div className="bg-gradient-to-br from-indigo-600 to-purple-600 px-8 pt-10 pb-14 text-center relative overflow-hidden">
                                {['✦','✧','★','✦','✧'].map((s, i) => (
                                    <motion.span
                                        key={i}
                                        className="absolute text-white/30 select-none pointer-events-none text-xl"
                                        style={{ left: `${10 + i * 20}%`, top: '8px' }}
                                        animate={{ y: [0, -50], opacity: [0.6, 0], scale: [1, 1.5] }}
                                        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.25, ease: 'easeOut' }}
                                    >
                                        {s}
                                    </motion.span>
                                ))}

                                <motion.div
                                    initial={{ scale: 0, rotate: -20 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    transition={{ delay: 0.1, type: 'spring', stiffness: 260, damping: 20 }}
                                    className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 border border-white/30 mb-5 text-4xl"
                                >
                                    🎉
                                </motion.div>
                                <motion.h1
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="text-2xl font-bold text-white mb-1"
                                >
                                    E-Mail bestätigt!
                                </motion.h1>
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.3 }}
                                    className="text-indigo-100 text-sm"
                                >
                                    Dein Konto ist jetzt aktiviert
                                </motion.p>
                            </div>

                            <div className="relative -mt-6 mx-6 bg-white rounded-2xl shadow-lg border border-gray-100 px-6 py-5 mb-6 text-center">
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    Willkommen bei <span className="font-bold text-indigo-600">Studytino</span>! 🚀
                                    Deine E-Mail-Adresse wurde erfolgreich bestätigt.
                                    Du kannst jetzt alle Funktionen nutzen.
                                </p>
                            </div>

                            <div className="px-8 pb-8 space-y-3">
                                <button
                                    onClick={() => navigate('/lernsets')}
                                    className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all text-sm flex items-center justify-center gap-2"
                                >
                                    Jetzt loslegen →
                                </button>
                                <p className="text-xs text-gray-400 text-center">
                                    Automatische Weiterleitung in {countdown} Sekunde{countdown !== 1 ? 'n' : ''}…
                                </p>
                            </div>
                        </motion.div>
                    )}

                    {/* Expired */}
                    {status === 'expired' && (
                        <motion.div
                            key="expired"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 overflow-hidden"
                        >
                            <div className="bg-gradient-to-br from-amber-500 to-orange-500 px-8 pt-10 pb-14 text-center relative overflow-hidden">
                                <motion.div
                                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 200 }}
                                    className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 border border-white/30 mb-5 text-4xl"
                                >
                                    ⏰
                                </motion.div>
                                <h1 className="text-2xl font-bold text-white mb-1">Link abgelaufen</h1>
                                <p className="text-amber-100 text-sm">Der Bestätigungslink ist nicht mehr gültig</p>
                            </div>

                            <div className="relative -mt-6 mx-6 bg-white rounded-2xl shadow-lg border border-gray-100 px-6 py-5 mb-6 text-center">
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    E-Mail-Bestätigungslinks sind nur 24 Stunden gültig.
                                    Bitte melde dich an und fordere einen neuen Link an.
                                </p>
                            </div>

                            <div className="px-8 pb-8 space-y-3">
                                <Link
                                    to="/login"
                                    className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all text-sm flex items-center justify-center"
                                >
                                    Zum Login
                                </Link>
                            </div>
                        </motion.div>
                    )}

                    {/* Error */}
                    {status === 'error' && (
                        <motion.div
                            key="error"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 overflow-hidden"
                        >
                            <div className="bg-gradient-to-br from-red-500 to-rose-600 px-8 pt-10 pb-14 text-center relative overflow-hidden">
                                <motion.div
                                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 200 }}
                                    className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 border border-white/30 mb-5 text-4xl"
                                >
                                    ⚠️
                                </motion.div>
                                <h1 className="text-2xl font-bold text-white mb-1">Etwas ist schiefgelaufen</h1>
                                <p className="text-red-100 text-sm">Bestätigung fehlgeschlagen</p>
                            </div>

                            <div className="relative -mt-6 mx-6 bg-white rounded-2xl shadow-lg border border-gray-100 px-6 py-5 mb-6 text-center">
                                <p className="text-gray-600 text-sm leading-relaxed mb-2">
                                    {errorMsg || 'Der Bestätigungslink ist ungültig oder wurde bereits verwendet.'}
                                </p>
                                <p className="text-gray-400 text-xs">
                                    Bitte fordere einen neuen Bestätigungslink an.
                                </p>
                            </div>

                            <div className="px-8 pb-8 space-y-3">
                                <Link
                                    to="/verify-email"
                                    className="w-full py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all text-sm flex items-center justify-center"
                                >
                                    Neuen Link anfordern
                                </Link>
                                <Link
                                    to="/login"
                                    className="w-full py-3 border border-gray-200 text-gray-500 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm flex items-center justify-center"
                                >
                                    Zum Login
                                </Link>
                            </div>
                        </motion.div>
                    )}

                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default EmailBestaetigtPage;